import React, { useState, useEffect } from "react";
import axios from "axios";
import { Alert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import EntityForm from "../../components/modals/EntityForm";
import { toast } from "react-toastify";
import { Plus, Search, Pencil } from "lucide-react";

const ITEMS_PER_PAGE = 8;

const ClientesPage = () => {
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/util";

  const fetchClientes = async () => {
    try {
      const res = await axios.get(`${API_URL}/clientes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientes(res.data);
      setFilteredClientes(res.data);
    } catch (err) {
      setError("Error al obtener clientes");
      toast.error(err.response?.data?.message || err.message || "Error desconocido");
    }
  };

  useEffect(() => { fetchClientes(); }, []); // eslint-disable-line

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFilteredClientes(clientes.filter((c) =>
      (c.nombre || "").toLowerCase().includes(lower) ||
      (c.nit || "").toLowerCase().includes(lower) ||
      (c.razon || "").toLowerCase().includes(lower)
    ));
    setCurrentPage(1);
  }, [searchTerm, clientes]);

  const totalPages = Math.ceil(filteredClientes.length / ITEMS_PER_PAGE);
  const currentClientes = filteredClientes.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleSave = async (formData) => {
    try {
      if (selectedCliente) {
        await axios.put(`${API_URL}/clientes/${selectedCliente.clte_codigo}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Cliente actualizado correctamente");
      } else {
        await axios.post(`${API_URL}/clientes`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Cliente creado correctamente");
      }
      setShowModal(false);
      fetchClientes();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || err.message || "Error inesperado");
    }
  };

  return (
    <div className="space-y-4">
      {error && <Alert type="error">{error}</Alert>}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-400" />
          <input
            type="text"
            placeholder="Buscar cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 border border-brand-200 rounded-xl py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <Button onClick={() => { setSelectedCliente(null); setShowModal(true); }} className="gap-2">
          <Plus className="h-4 w-4" /> Nuevo cliente
        </Button>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-xl border border-brand-100">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-brand-gradient text-white">
              {["Código", "Nombre", "NIT", "Correo", "Teléfono", "Teléfono 2", ""].map((h, i) => (
                <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${i === 6 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-50">
            {currentClientes.length > 0 ? currentClientes.map((c, i) => (
              <tr key={c.clte_codigo} className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-semibold">{c.clte_codigo}</span>
                </td>
                <td className="px-4 py-3 font-medium text-gray-800">{c.nombre}</td>
                <td className="px-4 py-3 text-gray-600">{c.nit}</td>
                <td className="px-4 py-3 text-gray-600">{c.email || "—"}</td>
                <td className="px-4 py-3 text-gray-600">{c.telefono || "—"}</td>
                <td className="px-4 py-3 text-gray-600">{c.telefono1 || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="outline" size="sm" onClick={() => { setSelectedCliente(c); setShowModal(true); }} className="gap-1">
                    <Pencil className="h-3 w-3" /> Editar
                  </Button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="7" className="py-8 text-center text-gray-400 italic text-sm">No se encontraron clientes.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>{currentClientes.length} de {filteredClientes.length}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>← Anterior</Button>
            <span className="px-3 py-1.5 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 font-medium">{currentPage}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Siguiente →</Button>
          </div>
        </div>
      )}

      {showModal && (
        <EntityForm type="cliente" entity={selectedCliente} onClose={() => setShowModal(false)} onSave={handleSave} />
      )}
    </div>
  );
};

export default ClientesPage;
